import { ReactNode, useEffect, useState } from 'react'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import equipmentApi from '@/pages/Equipment/equipment.api.ts'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import AnalyticsProgressCard from '@/shared/Analytics/AnalyticsProgressCard'
import TextStatisticCard from '@/shared/Analytics/TextStatisticCard'
import LiftAnalyticsRatingTableCard from '@/pages/Equipment/pages/LiftPage/components/LiftAnalyticsRatingTableCard'
import formatUtils from '@/utils/format.utils.ts'
import DropdownInput from '@/shared/Inputs/DropdownInput'
import { IOption } from '@/types/global'
import liftPageConstants from '../../liftPage.constants'
import liftPageUtils from '../../liftPage.utils'

interface IProps {
  equipmentId: IEquipment['id']
}

const LiftAnalyticsCardContent = (props: IProps) => {
  const { wait: waitUpTimeRequest, loading: upTimeLoading } = useHttpLoader()

  const { wait: waitWarningsCountRequest, loading: warningCountLoading } = useHttpLoader()
  const { wait: waitErrorsCountRequest, loading: errorsCountLoading } = useHttpLoader()
  const { wait: waitFatalErrorsCountRequest, loading: fatalErrorsCountLoading } = useHttpLoader()

  const { wait: waitWarningsRatingRequest, loading: warningRatingLoading } = useHttpLoader()
  const { wait: waitErrorsRatingRequest, loading: errorsRatingLoading } = useHttpLoader()
  const { wait: waitFatalErrorsRatingRequest, loading: fatalErrorsRatingLoading } = useHttpLoader()

  const [errorsCount, setErrorsCount] = useState({ warnings: 0, errors: 0, fatalFaults: 0 })
  const [ratings, setRatings] = useState({ warnings: {}, errors: {}, fatalFaults: {} })
  const [upTimeStats, setUpTimeStats] = useState({ totalDownTime: 0, upTimePercentage: 0 })

  const [errorsCountDaysRangeOptions, setErrorsCountDaysRangeOptions] = useState({
    warningsDaysRange: liftPageConstants.dateOptions[1].value,
    errorsDaysRange: liftPageConstants.dateOptions[1].value,
    fatalFaultsDaysRange: liftPageConstants.dateOptions[1].value,
  })

  const [ratingsDaysRangeOptions, setRatingsDaysRangeOptions] = useState({
    warningsDaysRange: liftPageConstants.dateOptions[1].value,
    errorsDaysRange: liftPageConstants.dateOptions[1].value,
    fatalFaultsDaysRange: liftPageConstants.dateOptions[1].value,
  })

  const currentTS = new Date().getTime()

  // При изменении errorsCountDaysRangeOptions

  useEffect(() => {
    waitWarningsCountRequest(
      equipmentApi.getLiftWarningsCount(
        props.equipmentId,
        liftPageUtils.getPrevDays(errorsCountDaysRangeOptions.warningsDaysRange, currentTS),
        currentTS
      ),
      (resp) => {
        if (resp.status === 'success') {
          setErrorsCount((prev) => ({ ...prev, warnings: resp.body.count }))
        }
      }
    )
  }, [errorsCountDaysRangeOptions.warningsDaysRange])

  useEffect(() => {
    waitErrorsCountRequest(
      equipmentApi.getLiftErrorsCount(
        props.equipmentId,
        liftPageUtils.getPrevDays(errorsCountDaysRangeOptions.errorsDaysRange, currentTS),
        currentTS
      ),
      (resp) => {
        if (resp.status === 'success') {
          setErrorsCount((prev) => ({ ...prev, errors: resp.body.count }))
        }
      }
    )
  }, [errorsCountDaysRangeOptions.errorsDaysRange])

  useEffect(() => {
    waitFatalErrorsCountRequest(
      equipmentApi.getLiftFatalErrorsCount(
        props.equipmentId,
        liftPageUtils.getPrevDays(errorsCountDaysRangeOptions.fatalFaultsDaysRange, currentTS),
        currentTS
      ),
      (resp) => {
        if (resp.status === 'success') {
          setErrorsCount((prev) => ({ ...prev, fatalFaults: resp.body.count }))
        }
      }
    )
  }, [errorsCountDaysRangeOptions.fatalFaultsDaysRange])

  // При изменении ratingsDaysRangeOptions

  useEffect(() => {
    waitWarningsRatingRequest(
      equipmentApi.getLiftWarningsRating(
        props.equipmentId,
        liftPageUtils.getPrevDays(ratingsDaysRangeOptions.warningsDaysRange, currentTS),
        currentTS
      ),
      (resp) => {
        if (resp.status === 'success') {
          setRatings((prev) => ({ ...prev, warnings: resp.body }))
        }
      }
    )
  }, [ratingsDaysRangeOptions.warningsDaysRange])

  useEffect(() => {
    waitErrorsRatingRequest(
      equipmentApi.getLiftErrorsRating(
        props.equipmentId,
        liftPageUtils.getPrevDays(ratingsDaysRangeOptions.errorsDaysRange, currentTS),
        currentTS
      ),
      (resp) => {
        if (resp.status === 'success') {
          setRatings((prev) => ({ ...prev, errors: resp.body }))
        }
      }
    )
  }, [ratingsDaysRangeOptions.errorsDaysRange])

  useEffect(() => {
    waitFatalErrorsRatingRequest(
      equipmentApi.getLiftFatalErrorsRating(
        props.equipmentId,
        liftPageUtils.getPrevDays(ratingsDaysRangeOptions.fatalFaultsDaysRange, currentTS),
        currentTS
      ),
      (resp) => {
        if (resp.status === 'success') {
          setRatings((prev) => ({ ...prev, fatalFaults: resp.body }))
        }
      }
    )
  }, [ratingsDaysRangeOptions.fatalFaultsDaysRange])

  useEffect(() => {
    waitUpTimeRequest(equipmentApi.getLiftUptimeStats(props.equipmentId), (resp) => {
      if (resp.status === 'success') {
        setUpTimeStats(resp.body)
      }
    })
  }, [])

  const getColor = () => {
    if (upTimeStats.upTimePercentage < 50) {
      return 'danger' as const
    }

    if (upTimeStats.upTimePercentage < 80) {
      return 'warning' as const
    }

    return 'success' as const
  }

  const renderStatisticCardTitle = (
    title: string | ReactNode,
    dropDownProps: { options: IOption<number>[]; selectedOption: number; onSelect: (option: number) => void }
  ) => {
    return (
      <p>
        <span>
          {title}
          <span>{dropDownProps.selectedOption === 1 ? 'за ' : 'за последние '}</span>
        </span>
        <DropdownInput
          wrapperStyle={{ width: 'auto' }}
          menuStyle={{ width: '128px' }}
          title={'Выберите период'}
          type="inline"
          {...dropDownProps}
        />
      </p>
    )
  }

  return (
    <div className="flex-lines gap16" style={{ minHeight: 0, overflow: 'auto', paddingRight: '24px' }}>
      <div style={{ gridTemplateColumns: 'repeat(4, 1fr)', display: 'grid', gap: '16px' }}>
        <AnalyticsProgressCard
          title={'Бесперебойная работа '}
          labelTooltipText="Метрика бесперебойной работы (uptime percantage) отражает процент времени, в течении которого лифт подключен и шлет телеметрию. Например, лифт подключился в 10:00, сейчас 20:00 того же числа. Из-за проблем с интернетом лифт отключился с 11:00 до 12:00. В таком случае, он работал 9 из 10 часов. Метрика равна 90%."
          circleConfig={{ value: upTimeStats.upTimePercentage, size: 'large', type: getColor() }}
          loading={upTimeLoading}
          width="100%"
          height="186px"
          style={{ width: '100%', flexShrink: 0, height: '186px' }}
        />

        <TextStatisticCard
          title={renderStatisticCardTitle('Кол-во предупреждений ', {
            options: liftPageConstants.dateOptions,
            selectedOption: errorsCountDaysRangeOptions.warningsDaysRange,
            onSelect: (e) => setErrorsCountDaysRangeOptions((prev) => ({ ...prev, warningsDaysRange: e })),
          })}
          loading={warningCountLoading}
          width="100%"
          style={{ width: '100%', height: '186px' }}
        >
          <div style={{ marginTop: 'auto' }}>
            <span className="text-large-stat text-warning">{formatUtils.formatNumber(errorsCount.warnings)}</span>
          </div>
        </TextStatisticCard>
        <TextStatisticCard
          title={renderStatisticCardTitle('Кол-во ошибок ', {
            options: liftPageConstants.dateOptions,
            selectedOption: errorsCountDaysRangeOptions.errorsDaysRange,
            onSelect: (e) => setErrorsCountDaysRangeOptions((prev) => ({ ...prev, errorsDaysRange: e })),
          })}
          loading={errorsCountLoading}
          width="100%"
          style={{ width: '100%', height: '186px' }}
        >
          <div style={{ marginTop: 'auto' }}>
            <span className="text-large-stat text-error">{formatUtils.formatNumber(errorsCount.errors)}</span>
          </div>
        </TextStatisticCard>
        <TextStatisticCard
          title={renderStatisticCardTitle('Кол-во критических ошибок ', {
            options: liftPageConstants.dateOptions,
            selectedOption: errorsCountDaysRangeOptions.fatalFaultsDaysRange,
            onSelect: (e) => setErrorsCountDaysRangeOptions((prev) => ({ ...prev, fatalFaultsDaysRange: e })),
          })}
          loading={fatalErrorsCountLoading}
          width="100%"
          style={{ width: '100%', height: '186px' }}
        >
          <div style={{ marginTop: 'auto' }}>
            <span className="text-large-stat text-error">{formatUtils.formatNumber(errorsCount.fatalFaults)}</span>
          </div>
        </TextStatisticCard>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <LiftAnalyticsRatingTableCard
          rating={ratings.warnings}
          cardTitle={renderStatisticCardTitle(
            <>
              <span>Топ </span>
              <span className="text-warning">предупреждений </span>
            </>,
            {
              options: liftPageConstants.dateOptions,
              selectedOption: ratingsDaysRangeOptions.warningsDaysRange,
              onSelect: (e) => setRatingsDaysRangeOptions((prev) => ({ ...prev, warningsDaysRange: e })),
            }
          )}
          loading={warningRatingLoading}
        />
        <LiftAnalyticsRatingTableCard
          rating={ratings.errors}
          cardTitle={renderStatisticCardTitle(
            <>
              <span>Топ </span>
              <span className="text-error-light">ошибок </span>
            </>,
            {
              options: liftPageConstants.dateOptions,
              selectedOption: ratingsDaysRangeOptions.errorsDaysRange,
              onSelect: (e) => setRatingsDaysRangeOptions((prev) => ({ ...prev, errorsDaysRange: e })),
            }
          )}
          loading={errorsRatingLoading}
        />
        <LiftAnalyticsRatingTableCard
          rating={ratings.fatalFaults}
          cardTitle={renderStatisticCardTitle(
            <>
              <span>Топ </span>
              <span className="text-error-light">критических ошибок </span>
            </>,
            {
              options: liftPageConstants.dateOptions,
              selectedOption: ratingsDaysRangeOptions.fatalFaultsDaysRange,
              onSelect: (e) => setRatingsDaysRangeOptions((prev) => ({ ...prev, fatalFaultsDaysRange: e })),
            }
          )}
          loading={fatalErrorsRatingLoading}
        />
      </div>
    </div>
  )
}

export default LiftAnalyticsCardContent
